/**
 * Central settings: single place for defaults, local and optional account storage.
 *
 * How to add a new feature's settings:
 * 1. In types.ts: add interface XSettings and extend SettingsDomain.
 * 2. In defaults.ts: add X_DEFAULTS and add to SETTINGS_DEFAULTS; set default values (e.g. lang: 'en').
 * 3. In storage.ts: add LOCAL_KEYS entries for the new domain keys; add coerce() logic if needed.
 * 4. Use getSetting(domain, key) / setSetting(domain, key) in the feature (or a thin feature-settings wrapper).
 *
 * Persistence:
 * - local: getSetting/setSetting use localStorage (no account). Good for device-only prefs.
 * - account: register AccountSettingsReader; when set, getSetting returns account value first. Writing to
 *   account is done by the feature (e.g. updateUserPrefs in Firestore), not setSetting (setSetting only writes local).
 */

export { TTS_DEFAULTS, APPEARANCE_DEFAULTS, SETTINGS_DEFAULTS, getDefault } from './defaults';
export { getSetting, setSetting, getRawLocal, registerAccountSettingsReader, registerAccountSettingsWriter } from './storage';
export type { TTSSettings, AppearanceSettings, SettingsDomain, StorageKind } from './types';

/** Highlight color options (shared by TTS and appearance). Same as plugin/notes-tts. */
export const HIGHLIGHT_COLORS = Object.freeze([
  { id: 'purple', label: 'Purple', rgb: '124, 58, 237' },
  { id: 'violet', label: 'Violet', rgb: '139, 92, 246' },
  { id: 'indigo', label: 'Indigo', rgb: '99, 102, 241' },
  { id: 'blue', label: 'Blue', rgb: '59, 130, 246' },
  { id: 'cyan', label: 'Cyan', rgb: '6, 182, 212' },
  { id: 'teal', label: 'Teal', rgb: '20, 184, 166' },
  { id: 'green', label: 'Green', rgb: '34, 197, 94' },
  { id: 'lime', label: 'Lime', rgb: '132, 204, 22' },
  { id: 'yellow', label: 'Yellow', rgb: '234, 179, 8' },
  { id: 'amber', label: 'Amber', rgb: '245, 158, 11' },
  { id: 'orange', label: 'Orange', rgb: '249, 115, 22' },
  { id: 'red', label: 'Red', rgb: '239, 68, 68' },
  { id: 'rose', label: 'Rose', rgb: '244, 63, 94' },
  { id: 'pink', label: 'Pink', rgb: '236, 72, 153' }
] as const);
